import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, HeartPulse, Activity, Droplet, Info } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDateTime } from "@/lib/format";
import { PageHero } from "@/components/premium/UI";
import { usePermissions } from "@/lib/permissions";
import { RowActions } from "@/components/premium/RowActions";

const WINDOWS = [
  { value: "morning", label: "Morning (06:30)" },
  { value: "mid_morning", label: "Mid morning" },
  { value: "afternoon", label: "Afternoon (13:00)" },
  { value: "mid_afternoon", label: "Mid afternoon (15:00)" },
  { value: "evening", label: "Evening (20:00)" },
];

export default function Health() {
  const { has } = usePermissions();
  const canWrite = has("data:health:write");
  const [dash, setDash] = useState(null);

  async function refresh() {
    try {
      const { data } = await api.get("/health/dashboard");
      setDash(data);
    } catch { toast.error("Could not load Health360"); }
  }
  useEffect(() => { refresh(); }, []);

  const patient = dash?.patient;
  const today = dash?.today || [];
  const trend = (dash?.trend || []).map((r) => ({
    when: (r.recorded_at || "").slice(5, 16).replace("T", " "),
    bp: r.bp_systolic || null,
    sugar: r.blood_sugar || null,
  }));

  return (
    <div className="space-y-6" data-testid="health-page">
      <PageHero
        eyebrow="Health360"
        title="Mother's health monitoring"
        subtitle="Home360 tracks readings only. It never diagnoses or prescribes."
        moduleKind="health"
      />

      {patient && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="kpi-card md:col-span-2" data-testid="patient-card">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-deepgreen/10 text-deepgreen flex items-center justify-center"><HeartPulse className="h-6 w-6" /></div>
              <div className="flex-1">
                <div className="font-heading text-xl font-semibold tracking-tight">{patient.name}</div>
                <div className="subtle">Age {patient.age} · {patient.doctor} · {patient.hospital}</div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {(patient.conditions || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)}
                </div>
              </div>
            </div>
          </div>
          <div className="kpi-card">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Med. compliance</div>
            <div className="mt-2 font-heading text-3xl font-semibold">{fmt(dash.compliance_pct, 1)}%</div>
            <div className="subtle mt-1">Last 14 days</div>
          </div>
          <div className="kpi-card">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Today's readings</div>
            <div className="mt-2 font-heading text-3xl font-semibold">{today.length}/5</div>
            <div className="subtle mt-1">Expected windows</div>
          </div>
        </div>
      )}

      <Tabs defaultValue="capture">
        <TabsList className="grid grid-cols-3 max-w-lg">
          <TabsTrigger value="capture" data-testid="health-tab-capture">Capture reading</TabsTrigger>
          <TabsTrigger value="today" data-testid="health-tab-today">Today</TabsTrigger>
          <TabsTrigger value="trend" data-testid="health-tab-trend">Trend</TabsTrigger>
        </TabsList>

        <TabsContent value="capture" className="mt-6">
          <CaptureReading patient={patient} onSaved={refresh} />
        </TabsContent>

        <TabsContent value="today" className="mt-6">
          <Card className="rounded-2xl">
            <CardHeader><CardTitle>Today's readings</CardTitle><CardDescription>Chronological</CardDescription></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>When</TableHead><TableHead>Window</TableHead><TableHead>BP</TableHead>
                  <TableHead>Pulse</TableHead><TableHead>Sugar</TableHead><TableHead>Meds</TableHead><TableHead>Alerts</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {today.map((r) => (
                    <TableRow key={r.id} data-testid={`reading-row-${r.id}`}>
                      <TableCell>{fmtDateTime(r.recorded_at)}</TableCell>
                      <TableCell>{r.window}</TableCell>
                      <TableCell>{r.bp_systolic || "—"}/{r.bp_diastolic || "—"}</TableCell>
                      <TableCell>{r.pulse || "—"}</TableCell>
                      <TableCell>{r.blood_sugar || "—"}</TableCell>
                      <TableCell>{r.medication_taken ? "Yes" : "No"}</TableCell>
                      <TableCell>{(r.alerts || []).length > 0 ? (<span className="inline-flex items-center gap-1 text-destructive text-xs"><AlertTriangle className="h-3 w-3" />{r.alerts.length}</span>) : "—"}</TableCell>
                      <TableCell className="text-right">
                        <RowActions
                          row={r} canWrite={canWrite}
                          basePath="/health/readings" resource="health-reading"
                          editableFields={["window", "bp_systolic", "bp_diastolic", "pulse", "blood_sugar", "water_intake_ml", "meal", "activity", "general_condition", "medication_taken", "symptoms"]}
                          renderEditForm={(s, setS) => (
                            <div className="grid grid-cols-2 gap-3">
                              <div className="col-span-2">
                                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Window</Label>
                                <Select value={s.window ?? ""} onValueChange={(v) => setS({ ...s, window: v })}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>{WINDOWS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <Field label="BP systolic"><Input type="number" value={s.bp_systolic ?? ""} onChange={(e) => setS({ ...s, bp_systolic: Number(e.target.value) })} data-testid="edit-bp-sys" /></Field>
                              <Field label="BP diastolic"><Input type="number" value={s.bp_diastolic ?? ""} onChange={(e) => setS({ ...s, bp_diastolic: Number(e.target.value) })} data-testid="edit-bp-dia" /></Field>
                              <Field label="Pulse"><Input type="number" value={s.pulse ?? ""} onChange={(e) => setS({ ...s, pulse: Number(e.target.value) })} /></Field>
                              <Field label="Blood sugar"><Input type="number" step="0.1" value={s.blood_sugar ?? ""} onChange={(e) => setS({ ...s, blood_sugar: Number(e.target.value) })} /></Field>
                              <Field label="General condition"><Input value={s.general_condition ?? ""} onChange={(e) => setS({ ...s, general_condition: e.target.value })} /></Field>
                              <div className="col-span-2 flex items-center gap-2">
                                <Checkbox checked={!!s.medication_taken} onCheckedChange={(v) => setS({ ...s, medication_taken: !!v })} />
                                <Label className="text-sm cursor-pointer" onClick={() => setS({ ...s, medication_taken: !s.medication_taken })}>Medication taken</Label>
                              </div>
                              <div className="col-span-2"><Field label="Symptoms / notes"><Textarea value={s.symptoms ?? ""} onChange={(e) => setS({ ...s, symptoms: e.target.value })} /></Field></div>
                            </div>
                          )}
                          onChanged={refresh}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {today.length === 0 && <TableRow><TableCell colSpan={8} className="text-center subtle py-8">No readings today yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trend" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-2xl">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4 text-deepgreen" /> Blood pressure (systolic)</CardTitle></CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trend}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="when" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Line type="monotone" dataKey="bp" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="rounded-2xl">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Droplet className="h-4 w-4 text-deepgreen" /> Blood sugar (mmol/L)</CardTitle></CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={trend}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                    <XAxis dataKey="when" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Line type="monotone" dataKey="sugar" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CaptureReading({ patient, onSaved }) {
  const { has } = usePermissions();
  const canWrite = has("data:health:write");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    patient_id: "", window: "morning",
    bp_systolic: "", bp_diastolic: "", pulse: "", blood_sugar: "",
    medication_taken: false, meal: "", symptoms: "", activity: "",
    water_intake_ml: "", general_condition: "", notes: "",
  });
  useEffect(() => { if (patient) setForm((f) => ({ ...f, patient_id: patient.id })); }, [patient]);

  async function submit() {
    if (!form.patient_id) { toast.error("No patient loaded"); return; }
    const payload = { ...form };
    ["bp_systolic", "bp_diastolic", "pulse", "water_intake_ml"].forEach((k) => { payload[k] = payload[k] === "" ? null : Number(payload[k]); });
    payload.blood_sugar = payload.blood_sugar === "" ? null : Number(payload.blood_sugar);
    try {
      const { data } = await api.post("/health/readings", payload);
      if ((data.alerts || []).length) toast.warning(`Threshold alert: ${data.alerts.join(", ")}`);
      else toast.success("Reading captured");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div><CardTitle>Capture a reading</CardTitle><CardDescription>Select the correct time window.</CardDescription></div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canWrite ? (
            <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-reading-btn">Log reading</Button></DialogTrigger>
          ) : (
            <Button className="opacity-50 cursor-not-allowed" disabled title="Read-only in this module" data-testid="new-reading-btn-disabled">Log reading</Button>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>New health reading</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Time window</Label>
                <Select value={form.window} onValueChange={(v) => setForm({ ...form, window: v })}>
                  <SelectTrigger data-testid="reading-window-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WINDOWS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="BP systolic"><Input type="number" value={form.bp_systolic} onChange={(e) => setForm({ ...form, bp_systolic: e.target.value })} data-testid="reading-bp-sys" /></Field>
              <Field label="BP diastolic"><Input type="number" value={form.bp_diastolic} onChange={(e) => setForm({ ...form, bp_diastolic: e.target.value })} data-testid="reading-bp-dia" /></Field>
              <Field label="Pulse"><Input type="number" value={form.pulse} onChange={(e) => setForm({ ...form, pulse: e.target.value })} /></Field>
              <Field label="Blood sugar"><Input type="number" step="0.1" value={form.blood_sugar} onChange={(e) => setForm({ ...form, blood_sugar: e.target.value })} data-testid="reading-sugar" /></Field>
              <Field label="Water intake (ml)"><Input type="number" value={form.water_intake_ml} onChange={(e) => setForm({ ...form, water_intake_ml: e.target.value })} /></Field>
              <Field label="Meal"><Input value={form.meal} onChange={(e) => setForm({ ...form, meal: e.target.value })} /></Field>
              <Field label="Activity"><Input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} /></Field>
              <Field label="General condition"><Input value={form.general_condition} onChange={(e) => setForm({ ...form, general_condition: e.target.value })} /></Field>
              <div className="col-span-2 flex items-center gap-2 mt-2">
                <Checkbox checked={form.medication_taken} onCheckedChange={(v) => setForm({ ...form, medication_taken: !!v })} data-testid="reading-meds" />
                <Label className="text-sm cursor-pointer" onClick={() => setForm({ ...form, medication_taken: !form.medication_taken })}>Medication taken for this window</Label>
              </div>
              <div className="col-span-2"><Field label="Symptoms / notes"><Textarea value={form.symptoms} onChange={(e) => setForm({ ...form, symptoms: e.target.value })} /></Field></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="reading-save-btn">Save reading</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {WINDOWS.map((w) => (
            <div key={w.value} className="rounded-xl border border-border p-4">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{w.label}</div>
              <div className="mt-2 text-sm">Log a reading for this window when it's due.</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
